import PropTypes from 'prop-types';
import { Link as RouterLink } from 'react-router-dom';
import { useEffect, useMemo } from 'react';
// @mui
import { alpha, styled } from '@mui/material/styles';
import { Avatar, Box, Button, Card, Grid, Skeleton, Stack, Typography } from '@mui/material';
// components
import Iconify from '../../../components/iconify';
import { MotionInView, varFadeInUp } from '../../../components/animate';
import { SectionHeader } from '../common';
// redux
import { useDispatch, useSelector } from '../../../redux/store';
import { getStudentwalls } from '../../../redux/slices/course';

// ----------------------------------------------------------------------

// Cards stay 3-up on the homepage grid but drop to 4-up (a quarter of the
// container) on wide screens when a page asks for it, or 2-up inside a narrow
// sidebar column. `showcase` is what every other page uses and is unchanged.
const VARIANT_CARD_SIZE = {
  showcase: { xs: 12, sm: 6, md: 6, lg: 4 },
  compact: { xs: 12, sm: 6, md: 4, lg: 3 },
  sidebar: { xs: 12, sm: 6, md: 6 },
};

const VARIANT_LIMIT = { showcase: 12, compact: 6, sidebar: 4 };

const REVEAL_TRANSITION = { duration: 0.5, ease: [0.43, 0.13, 0.23, 0.96] };
const revealDelay = (index) => Math.min(index, 5) * 0.07;

const MediaBox = styled('div')({
  position: 'relative',
  aspectRatio: '3 / 4',
  '& img': {
    width: '100%',
    height: '100%',
    objectFit: 'cover',
    display: 'block',
    transition: 'transform 0.7s cubic-bezier(0.43, 0.13, 0.23, 0.96)',
  },
});

// Caption plate. On touch devices there is no hover to reveal it, so it stays
// visible; on pointer devices it slides up on hover instead.
const CaptionPlate = styled('div')(({ theme }) => ({
  position: 'absolute',
  bottom: 0,
  left: 0,
  zIndex: 2,
  width: '100%',
  display: 'block',
  padding: theme.spacing(2),
  borderBottomLeftRadius: theme.shape.borderRadius,
  borderBottomRightRadius: theme.shape.borderRadius,
  backdropFilter: 'blur(8px)',
  WebkitBackdropFilter: 'blur(8px)',
  backgroundColor: alpha(theme.palette.grey[900], 0.72),
  '@media (hover: hover)': {
    opacity: 0,
    transform: 'translateY(14px)',
    transition: 'opacity 0.35s ease, transform 0.35s cubic-bezier(0.43, 0.13, 0.23, 0.96)',
    '.student-card:hover &': { opacity: 1, transform: 'translateY(0)' },
  },
  '@media (prefers-reduced-motion: reduce)': {
    opacity: 1,
    transform: 'none',
    transition: 'none',
  },
}));

// ----------------------------------------------------------------------
// StudentCard
// - `cardSize` is the responsive Grid sizing so a host page can run 3-up, 4-up
//   or 2-up without this component knowing which page it is on.
// - `dense` is for the 2-up sidebar variant: a squarer media box and a tighter
//   caption, so the name still fits at roughly half the column width.
// ----------------------------------------------------------------------
StudentCard.propTypes = {
  student: PropTypes.object,
  index: PropTypes.number,
  cardSize: PropTypes.object,
  dense: PropTypes.bool,
};

export function StudentCard({ student, index = 0, cardSize = VARIANT_CARD_SIZE.showcase, dense = false }) {
  const { user, course } = student ?? {};
  const name = user?.name;
  const avatar = user?.avatar;

  return (
    <Grid size={cardSize} sx={{ p: 0, display: 'flex' }}>
      <MotionInView
        variants={varFadeInUp}
        transition={{ ...REVEAL_TRANSITION, delay: revealDelay(index) }}
        sx={{ width: '100%', display: 'flex' }}
      >
        <Card
          className="student-card"
          sx={{
            position: 'relative',
            width: '100%',
            overflow: 'hidden',
            borderRadius: (theme) => theme.shape.borderRadius,
            boxShadow: (theme) => `0 10px 30px ${alpha(theme.palette.grey[900], 0.08)}`,
            transition: 'transform 0.4s cubic-bezier(0.43, 0.13, 0.23, 0.96), box-shadow 0.4s ease',
            '@media (hover: hover)': {
              '&:hover': {
                transform: 'translateY(-6px)',
                boxShadow: (theme) => `0 20px 40px ${alpha(theme.palette.grey[900], 0.16)}`,
              },
              '&:hover img': { transform: 'scale(1.07)' },
            },
            '@media (prefers-reduced-motion: reduce)': {
              transition: 'none',
              '&:hover': { transform: 'none' },
              '&:hover img': { transform: 'none' },
            },
          }}
        >
          <MediaBox sx={{ aspectRatio: dense ? '1 / 1' : '3 / 4' }}>
            {avatar ? (
              <Box component="img" alt={name || ''} src={avatar} loading="lazy" decoding="async" />
            ) : (
              <Stack sx={{ alignItems: 'center', justifyContent: 'center', height: '100%', color: 'text.disabled' }}>
                <Avatar sx={{ width: dense ? 40 : 56, height: dense ? 40 : 56, bgcolor: 'grey.300', color: 'common.white' }}>
                  {(name || '?').charAt(0).toUpperCase()}
                </Avatar>
              </Stack>
            )}

            <CaptionPlate sx={dense ? { p: 1.25 } : undefined}>
              <Typography
                variant={dense ? 'caption' : 'subtitle2'}
                sx={{
                  color: 'common.white',
                  textTransform: 'capitalize',
                  fontWeight: 600,
                  mb: 0.25,
                  lineHeight: 1.3,
                  display: '-webkit-box',
                  overflow: 'hidden',
                  WebkitLineClamp: 2,
                  WebkitBoxOrient: 'vertical',
                }}
              >
                {name}
              </Typography>
              {/* The course line is redundant on a course detail page, where
                  every card already belongs to the course being viewed. */}
              {course && !dense && (
                <Typography
                  variant="caption"
                  sx={{
                    color: (theme) => alpha(theme.palette.common.white, 0.78),
                    textTransform: 'uppercase',
                    display: 'block',
                  }}
                >
                  {course}
                </Typography>
              )}
            </CaptionPlate>
          </MediaBox>
        </Card>
      </MotionInView>
    </Grid>
  );
}

// ----------------------------------------------------------------------

function StudentCardSkeleton({ cardSize, dense = false }) {
  return (
    <Grid size={cardSize} sx={{ p: 0 }}>
      <Skeleton
        variant="rounded"
        width="100%"
        sx={{
          aspectRatio: dense ? '1 / 1' : '3 / 4',
          borderRadius: (theme) => theme.shape.borderRadius,
        }}
      />
    </Grid>
  );
}

StudentCardSkeleton.propTypes = { cardSize: PropTypes.object, dense: PropTypes.bool };

// ----------------------------------------------------------------------
// Homepage / course detail achievers showcase.
// - `variant` picks the presentation: `showcase` (homepage, 3-up), `compact`
//   (full-width quarter-of-screen strip), or `sidebar` (one per row inside a
//   narrow column). `limit` overrides the per-variant result cap.
// ----------------------------------------------------------------------
AchieversWidget.propTypes = {
  course: PropTypes.string,
  variant: PropTypes.oneOf(['showcase', 'compact', 'sidebar']),
  limit: PropTypes.number,
  eyebrow: PropTypes.string,
  title: PropTypes.string,
  description: PropTypes.string,
  titleComponent: PropTypes.string,
  showViewAll: PropTypes.bool,
  skipFetch: PropTypes.bool,
  sx: PropTypes.object,
};

export default function AchieversWidget({
  course,
  variant = 'showcase',
  limit,
  eyebrow = 'Some of our recent achievers',
  title = 'Achievers',
  description,
  titleComponent = 'h2',
  showViewAll = true,
  // Set by the homepage, where `getHomeBundle` already filled `studentwalls`
  // with this same list. Left off everywhere else: /achievers and the course
  // pages need the full, unpaginated wall.
  skipFetch = false,
  sx,
}) {
  const cardSize = VARIANT_CARD_SIZE[variant] ?? VARIANT_CARD_SIZE.showcase;
  const maxItems = limit ?? VARIANT_LIMIT[variant] ?? VARIANT_LIMIT.showcase;
  const isSidebar = variant === 'sidebar';
  // The 2-up sidebar cards are roughly half a column wide, so they get the
  // compact treatment rather than the full-size portrait card.
  const isDense = variant === 'sidebar';

  const dispatch = useDispatch();
  const { studentwalls, isLoading } = useSelector((state) => state.course);

  useEffect(() => {
    if (!skipFetch) {
      dispatch(getStudentwalls(course));
    }
  }, [dispatch, course, skipFetch]);

  const achievers = useMemo(() => {
    const list = Array.isArray(studentwalls) ? studentwalls : [];
    return list.filter(Boolean).slice(0, maxItems);
  }, [studentwalls, maxItems]);

  if (!achievers.length && isLoading) {
    return (
      <Box sx={{ width: '100%', ...sx }}>
        <Grid container spacing={isSidebar ? 2 : { xs: 2.5, sm: 3 }}>
          {Array.from({ length: Math.min(maxItems, 8) }, (_, index) => (
            <StudentCardSkeleton key={index} cardSize={cardSize} dense={isDense} />
          ))}
        </Grid>
      </Box>
    );
  }

  if (!achievers.length) {
    return null;
  }

  return (
    <Box component="section" sx={{ width: '100%', ...sx }}>
      <SectionHeader
        eyebrow={eyebrow}
        title={title}
        titleComponent={titleComponent}
        description={description}
        align={isSidebar ? 'left' : 'center'}
        sx={{ mb: isSidebar ? 2 : undefined }}
      />

      <Grid
        container
        spacing={isSidebar ? 2 : { xs: 2.5, sm: 3 }}
        sx={{ mt: isSidebar ? 0 : { xs: 0.5, md: 1 }, alignItems: 'stretch' }}
      >
        {achievers.map((student, index) => (
          <StudentCard
            key={student?.id ?? index}
            student={student}
            index={index}
            cardSize={cardSize}
            dense={isDense}
          />
        ))}
      </Grid>

      {showViewAll && (
        <Box sx={{ display: 'flex', justifyContent: isSidebar ? 'flex-start' : 'center', mt: isSidebar ? 2 : { xs: 3, md: 4 } }}>
          <Button
            component={RouterLink}
            to="/achievers"
            variant="outlined"
            size={isSidebar ? 'small' : 'medium'}
            endIcon={<Iconify icon="eva:arrow-forward-outline" />}
          >
            View more achievers
          </Button>
        </Box>
      )}
    </Box>
  );
}
